import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/Button'
import { Dialog } from '@/components/ui/Dialog'
import { ScrollableList } from '@/components/ui/ScrollableList'
import { FloatingPanel } from '@/components/ui/FloatingPanel'
import { EyeIcon, TrashIcon } from '@/components/ui/icons'
import { useModalStore } from '@/hooks/useModalStore'
import { useDiagramStore, useSelectionStore, useViewportStore } from '@/stores'
import type { Relation, Table } from '@/types'
import { isForeignKey } from '@/utils/columnKeys'
import { getTableHeight, getTableWidth } from '@/utils/geometry'

function focusRelationship(relation: Relation, tables: Table[]) {
  const fromTable = tables.find((table) => table.id === relation.from)
  const toTable = tables.find((table) => table.id === relation.to)
  if (!fromTable || !toTable) return

  const viewport = useViewportStore.getState()
  const centerX = (
    fromTable.x + getTableWidth(fromTable) / 2 +
    toTable.x + getTableWidth(toTable) / 2
  ) / 2
  const centerY = (
    fromTable.y + getTableHeight(fromTable) / 2 +
    toTable.y + getTableHeight(toTable) / 2
  ) / 2
  viewport.setPan(
    viewport.containerWidth / (2 * viewport.sc) - centerX,
    viewport.containerHeight / (2 * viewport.sc) - centerY,
  )
  useSelectionStore.getState().selectRelationship(relation.id)
}

export function FKPanel() {
  const { t } = useTranslation()
  const panel = useModalStore((state) => state.fkPanel)
  const close = useModalStore((state) => state.closeFkPanel)
  const tables = useDiagramStore((state) => state.tables)
  const rels = useDiagramStore((state) => state.rels)
  const updateTable = useDiagramStore((state) => state.updateTable)
  const deleteRelationship = useDiagramStore((state) => state.deleteRelationship)
  const [pendingDelete, setPendingDelete] = useState<{ relationId: string | null; columnName: string; orphan: boolean } | null>(null)

  const table = useMemo(
    () => tables.find((entry) => entry.id === panel.tableId) ?? null,
    [panel.tableId, tables],
  )

  const items = useMemo(() => {
    if (!table) return []
    return table.cols
      .filter((column) => isForeignKey(column))
      .map((column) => {
        const relation = rels.find((entry) => entry.from === table.id && entry.col === column.name)
        return {
          column,
          relation: relation ?? null,
        }
      })
  }, [rels, table])

  if (!table) return null

  return (
    <>
      <FloatingPanel
        open={panel.open}
        onClose={close}
        position={panel.position}
        title={t('details.foreignKeys')}
        width={340}
        variant="legacy"
        anchorX="left"
        headerClassName="px-[10px] py-[8px] [&>button]:h-5 [&>button]:w-5"
        bodyClassName="py-[8px]"
      >
        {items.length ? (
          <ScrollableList maxVisibleItems={5} itemHeight={38} className="space-y-[5px]">
            {items.map(({ column, relation }) => (
              <div
                key={column.name}
                className="flex items-center gap-1.5 rounded-[5px] border px-[7px] py-[5px]"
                style={{ borderColor: 'var(--border)', background: 'var(--bg-deep)' }}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex min-w-0 items-center gap-1">
                    <div className="truncate text-[10px] font-bold leading-none">{column.name}</div>
                    {relation ? (
                      <span
                        className="shrink-0 rounded-full px-[5px] py-[1px] text-[8px] font-bold leading-none"
                        style={{ background: 'rgba(79,70,229,.2)', color: '#818CF8' }}
                      >
                        {relation.type}
                      </span>
                    ) : null}
                  </div>
                  <div className="truncate text-[9px] leading-none" style={{ color: 'var(--text-dim)' }}>
                    {relation
                      ? `→ ${tables.find((tbl) => tbl.id === relation.to)?.fullName ?? relation.to}.${relation.toColumn || 'id'}`
                      : `→ ${t('details.orphanFk')}`}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  {relation ? (
                    <button
                      type="button"
                      onClick={() => focusRelationship(relation, tables)}
                      className="inline-flex h-5 w-5 items-center justify-center rounded-[3px] transition-colors"
                      style={{ color: 'var(--text-dim)' }}
                      title={t('details.viewRelation')}
                      aria-label={t('details.viewRelation')}
                    >
                      <EyeIcon />
                    </button>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => setPendingDelete({ relationId: relation?.id ?? null, columnName: column.name, orphan: !relation })}
                    className="inline-flex h-5 w-5 items-center justify-center rounded-[3px] transition-colors"
                    style={{ color: 'var(--color-danger)' }}
                    title={relation ? t('details.deleteFkRelation') : t('prompts.deleteFkColumn')}
                    aria-label={relation ? t('details.deleteFkRelation') : t('prompts.deleteFkColumn')}
                  >
                    <TrashIcon />
                  </button>
                </div>
              </div>
            ))}
          </ScrollableList>
        ) : (
          <div className="py-3 text-center text-[10px]" style={{ color: 'var(--text-dim)' }}>
            {t('details.noForeignKeys')}
          </div>
        )}
      </FloatingPanel>

      <Dialog
        open={!!pendingDelete}
        title={pendingDelete?.orphan ? t('prompts.deleteFkColumn') : t('details.deleteFkRelation')}
        onClose={() => setPendingDelete(null)}
        onConfirm={() => {
          if (!pendingDelete) return
          if (pendingDelete.relationId) {
            deleteRelationship(pendingDelete.relationId)
          } else {
            updateTable(table.id, {
              cols: table.cols.filter((column) => column.name !== pendingDelete.columnName),
            })
          }
          setPendingDelete(null)
          close()
        }}
        footer={(
          <>
            <Button variant="ghost" onClick={() => setPendingDelete(null)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                if (!pendingDelete) return
                if (pendingDelete.relationId) {
                  deleteRelationship(pendingDelete.relationId)
                } else {
                  updateTable(table.id, {
                    cols: table.cols.filter((column) => column.name !== pendingDelete.columnName),
                  })
                }
                setPendingDelete(null)
                close()
              }}
            >
              {t('common.delete')}
            </Button>
          </>
        )}
      >
        {pendingDelete?.orphan
          ? t('details.deleteFkColumnQuestion', { name: pendingDelete.columnName })
          : t('details.deleteFkRelationQuestion', { name: pendingDelete?.columnName })}
      </Dialog>
    </>
  )
}
